import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Scan, X, AlertTriangle, CheckCircle } from "lucide-react";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface AddFamilyProductDialogProps {
  parentProductId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (data: { parentId: string; childData: any }) => void;
}

export const AddFamilyProductDialog = ({
  parentProductId,
  open,
  onOpenChange,
  onAdd,
}: AddFamilyProductDialogProps) => {
  const [formData, setFormData] = useState({
    barcode: "",
    product_name: "",
    size_variant: "",
  });
  const [showScanner, setShowScanner] = useState(false);

  // Fetch parent product to pre-populate some fields
  const { data: parentProduct } = useQuery({
    queryKey: ['master-product', parentProductId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('master_products')
        .select('*, master_categories(category_name)')
        .or(`id.eq.${parentProductId},product_serial_number.eq.${parentProductId}`)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!parentProductId && open
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!parentProduct?.id) {
      console.error('❌ Parent product not found or missing ID');
      return;
    }
    
    console.log('🔍 Parent product:', parentProduct);
    
    // Child product inherits parent's category and other attributes
    const childData = {
      barcode: formData.barcode,
      product_name: formData.product_name,
      size_variant: formData.size_variant,
      category_id: parentProduct?.category_id, // Always inherit from parent
      data_source: 'family_variant',
    };

    console.log('📤 Submitting family product data:', { parentId: parentProduct.id, childData });
    onAdd({ parentId: parentProduct.id, childData }); // Always use the UUID from the found parent product
    onOpenChange(false);
    setFormData({
      barcode: "",
      product_name: "",
      size_variant: "",
    });
  };

  const handleScanResult = (barcode: string) => {
    setFormData(prev => ({ ...prev, barcode }));
    setShowScanner(false);
  };

  const isValid = formData.barcode && formData.product_name && formData.size_variant;
  const parentHasCategory = !!parentProduct?.category_id;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Size Variant</DialogTitle>
            <DialogDescription>
              Add a new size variant to the "{parentProduct?.product_name}" family.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Category Inheritance Alert */}
            {parentHasCategory ? (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Category will be inherited from parent: <strong>{parentProduct?.master_categories?.category_name}</strong>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Category will be inherited when parent gets assigned during submission.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="barcode">Barcode *</Label>
              <div className="flex gap-2">
                <Input
                  id="barcode"
                  value={formData.barcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, barcode: e.target.value }))}
                  placeholder="Enter barcode or scan"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowScanner(true)}
                >
                  <Scan className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="product_name">Product Name *</Label>
              <Input
                id="product_name"
                value={formData.product_name}
                onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
                placeholder="Enter product name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="size_variant">Size Variant *</Label>
              <Input
                id="size_variant"
                value={formData.size_variant}
                onChange={(e) => setFormData(prev => ({ ...prev, size_variant: e.target.value }))}
                placeholder="e.g., 6-Pack, 24-Pack, Family Size"
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isValid}>
                Add to Family
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <Dialog open={showScanner} onOpenChange={setShowScanner}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Scan Barcode</DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4"
                onClick={() => setShowScanner(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogHeader>
            <BarcodeScanner
              onScanResult={handleScanResult}
              onClose={() => setShowScanner(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};